import React, { useEffect, useState,useRef } from "react";
import { Box, Drawer, IconButton, Button, Checkbox, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import save from "../../../../assets/save.svg";
import WorkersMultiSelectDropdown from "../../../../components/workersMultiSelection";
import { BASE_URL } from "../../../../components/BaseUrl";
import { decryptToken } from "../../../../Utlis/Encryption";
import { useSelector } from "react-redux";
import {
  convertLocalDateTimeToUTC,
  convertUTCToLocalDateTime,
} from "../../../../components/dateTimeUtils";

const LOCAL_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const toLocalDateInputValue = (value) => {
  if (!value) return "";
  if (LOCAL_DATE_REGEX.test(value)) return value;
  const local = convertUTCToLocalDateTime(value, { format: "YYYY-MM-DD" });
  return local === "—" ? "" : local;
};

const buildFiltersForApi = (local) => {
  const { startDate, endDate, ...rest } = local;
  return {
    ...rest,
    startDate: startDate
      ? convertLocalDateTimeToUTC(`${startDate}T00:00:00`) || ""
      : "",
    endDate: endDate
      ? convertLocalDateTimeToUTC(`${endDate}T23:59:59`) || ""
      : "",
  };
};

const statusOptions = [
  { key: "assigned", label: "Assigned", value: "assigned" },
  { key: "in_progress", label: "In Progress", value: "in_progress" },
  { key: "completed", label: "Completed", value: "completed" },
  { key: "delayed", label: "Delayed", value: "delayed" },
  { key: "not_done", label: "Not Done", value: "not_done" },
  { key: "cancelled", label: "Cancelled", value: "cancelled" },
];

export default function TaskAttendanceHistoryFilterDrawer({
  open,
  onClose,
  filters,
  setFilters,
  setIsFilterApplied,
  onApplyFilters,
}) {
  const { t } = useTranslation();

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { company } = useSelector((state) => state.auth);
  const companyId = company?.id;

  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [localFilters, setLocalFilters] = useState(filters);
  const startRef = useRef(null);
  const endRef = useRef(null);
  // =======================
  // FETCH WORKERS
  // =======================
  const fetchWorkers = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/public/workers?no_pagination=true&company_id=${companyId}&status=active`,
        {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        }
      );

      const data = await res.json();
      const list = data?.data?.records || [];

      setWorkers([
        { label: t("all"), value: "all" },
        ...list.map((w) => ({
          label: `${w.first_name} ${w.last_name}`,
          value: w.id,
          email: w.email,
        })),
      ]);
    } catch (err) {
      console.error("Worker fetch error", err);
    }
  };

  // =======================
  // FETCH DEPARTMENTS
  // =======================
  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${BASE_URL}/departments?no_pagination=true`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });

      const data = await res.json();
      const list = data?.data?.departments || [];

      setDepartments([
        { label: t("all"), value: "all" },
        ...list.map((d) => ({
          label: d.name,
          value: d.id,
        })),
      ]);
    } catch (err) {
      console.error("Department fetch error", err);
    }
  };

  // =======================
  // SYNC ON OPEN
  // =======================
  useEffect(() => {
    if (open) {
      setLocalFilters({
        ...filters,
        startDate: toLocalDateInputValue(filters.startDate),
        endDate: toLocalDateInputValue(filters.endDate),
      });
      fetchWorkers();
      fetchDepartments();
    }
  }, [open, filters]);

  // =======================
  // APPLY FILTER
  // =======================
  const apply = () => {
    const apiFilters = buildFiltersForApi(localFilters);
    setFilters(apiFilters);
    setIsFilterApplied(
      Boolean(
        apiFilters.workerId ||
        apiFilters.department ||
        apiFilters.status ||
        apiFilters.startDate ||
        apiFilters.endDate ||
        apiFilters.groupByDate ||
        apiFilters.groupByWorker
      )
    );
    if (onApplyFilters) {
      onApplyFilters(apiFilters);
    }
    onClose();
  };

  // =======================
  // CLEAR FILTER
  // =======================
  const clear = () => {
    const cleared = {
      workerId: "",
      department: "",
      status: "",
      startDate: "",
      endDate: "",
      groupByDate: false,
      groupByWorker: false,
    };
    setLocalFilters(cleared);
    setFilters(cleared);
    setIsFilterApplied(false);
    if (onApplyFilters) {
      onApplyFilters(cleared);
    }
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400, md: 550 },
          backgroundColor: "#E7EBEE",
        },
      }}
    >
      {/* ================= HEADER ================= */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "#E7EBEE",
          zIndex: 10,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #ccc",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
            }}
          >
            {t("taskAttendanceHistory.filter.title")}
          </CustomText>
        </Box>
      </Box>

      {/* ================= BODY ================= */}
      <Box sx={{ px: 2 }}>
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            px: 3,
            py: 3,
            height: "calc(100vh - 90px)",
            overflowY: "auto",
          }}
        >
          {/* ===== WORKER ===== */}
          <Box mt={2}>
            <WorkersMultiSelectDropdown
              label={t("employee")}
              values={localFilters.workerId ? [localFilters.workerId] : []}
              options={workers}
              enableSearch
              onChange={(e) => {
                const value = e.target.value?.[0] || "";
                setLocalFilters((p) => ({
                  ...p,
                  workerId: value === "all" ? "" : value,
                }));
              }}
              renderValue={(selected) =>
                workers.find((w) => w.value === selected[0])?.label || ""
              }
              renderOption={(props, option) => (
                <li {...props}>
                  <span>{option.label}</span>
                  {option.email && (
                    <span style={{ fontSize: 12, color: "#888" }}>
                      {option.email}
                    </span>
                  )}
                </li>
              )}
            />
          </Box>

          {/* ===== DEPARTMENT ===== */}
          <Box mt={3}>
            <label className="field_label">
              {t("taskAttendanceHistory.filter.department")}
            </label>
            <select
              value={localFilters.department || ""}
              onChange={(e) =>
                setLocalFilters((p) => ({
                  ...p,
                  department: e.target.value === "all" ? "" : e.target.value,
                }))
              }
              className="project_input"
              style={{ width: "100%", height: 40 }}
            >
              {departments.map((dept) => (
                <option key={dept.value} value={dept.value}>
                  {dept.label}
                </option>
              ))}
            </select>
          </Box>

          {/* ===== STATUS ===== */}
          <Box mt={3}>
            <CustomText
              sx={{
                ...textStyles.h1,
                fontSize: 18,
                fontWeight: 500,
                color: "#2C384C",
                textAlign: "left",
              }}
            >
              {t("taskAttendanceHistory.filter.statusHeading")}
            </CustomText>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
              {statusOptions.map((opt) => {
                const isSelected = localFilters.status === opt.value;
                // Map status values to translation keys (camelCase)
                const translationKeyMap = {
                  "in_progress": "inProgress",
                  "not_done": "notDone",
                };
                const translationKey = translationKeyMap[opt.value] || opt.value;
                return (
                  <Button
                    key={opt.key}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const newStatus = isSelected ? "" : opt.value;
                      setLocalFilters((prev) => ({ ...prev, status: newStatus }));
                    }}
                    sx={{
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontFamily: "Poppins, sans-serif",
                      textTransform: "none",
                      py: 0.8,
                      px: 2,
                      color: isSelected ? "#006EC2" : "#363333",
                      border: isSelected
                        ? "2px solid #006EC2"
                        : "2px solid #091E4224",
                      backgroundColor: "transparent",
                    }}
                  >
                    {t(`taskAttendanceHistory.status.${translationKey}`) || opt.label}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {/* ===== DATE FROM ===== */}
          <Box mt={3}>
            <label className="field_label">
              {t("filterDrawer.startDate")}
            </label>
            <input
             ref={startRef}
                onClick={() => startRef.current?.showPicker()} 
              type="date"
              value={localFilters.startDate || ""}
              onChange={(e) =>
                setLocalFilters((p) => ({
                  ...p,
                  startDate: e.target.value,
                }))
              }
              className="project_input"
              style={{ width: "100%", height: 40 }}
            />
          </Box>

          {/* ===== DATE TO ===== */}
          <Box mt={3}>
            <label className="field_label">
              {t("filterDrawer.endDate")}
            </label>
            <input
             ref={endRef}
                onClick={() => endRef.current?.showPicker()} 
              type="date"
              value={localFilters.endDate || ""}
              onChange={(e) =>
                setLocalFilters((p) => ({
                  ...p,
                  endDate: e.target.value,
                }))
              }
              className="project_input"
              style={{ width: "100%", height: 40 }}
            />
          </Box>

          {/* ===== GROUPING OPTIONS ===== */}
          <Box mt={3}>
            <CustomText
              sx={{
                ...textStyles.h1,
                fontSize: 18,
                fontWeight: 500,
                color: "#2C384C",
                textAlign: "left",
              }}
            >
              {t("taskAttendanceHistory.filter.groupingHeading")}
            </CustomText>
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localFilters.groupByDate || false}
                    onChange={(e) =>
                      setLocalFilters((p) => ({
                        ...p,
                        groupByDate: e.target.checked,
                        groupByWorker: e.target.checked ? false : p.groupByWorker,
                      }))
                    }
                  />
                }
                label={t("taskAttendanceHistory.filter.groupByDate")}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    color: "#2C384C",
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localFilters.groupByWorker || false}
                    onChange={(e) =>
                      setLocalFilters((p) => ({
                        ...p,
                        groupByWorker: e.target.checked,
                        groupByDate: e.target.checked ? false : p.groupByDate,
                      }))
                    }
                  />
                }
                label={t("taskAttendanceHistory.filter.groupByWorker")}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    color: "#2C384C",
                  },
                }}
              />
            </Box>
          </Box>

          {/* ===== BUTTONS ===== */}
          <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
            <CustomButton
              onClick={apply}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img src={save} alt="save" width={16} height={16} />
                  {t("taskAttendanceHistory.filter.apply")}
                </Box>
              }
            />
            <Button
              variant="outlined"
              onClick={clear}
              sx={{
                fontSize: 15,
                textTransform: "none",
                borderRadius: "5px",
                color: "#006EC2",
                border: "2px solid #006EC2",
                px: 3,
              }}
            >
              {t("taskAttendanceHistory.filter.clear")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
